import clsx from 'clsx';
import type { ReactNode } from 'react';

import s from './StatusPill.module.scss';

export type StatusTone = 'neutral' | 'info' | 'progress' | 'success' | 'warning' | 'danger';

export interface StatusPillProps {
  label: string;
  tone?: StatusTone;
  /**
   * A glyph before the label, for a value the shape can carry faster than the word —
   * a priority arrow, a lock. Decorative: the label already says what this is, so pass
   * it `aria-hidden`.
   */
  icon?: ReactNode;
  /**
   * `outline` drops the fill for a hairline and moves the tone onto the glyph, leaving
   * the label in body ink. For a column that sits beside a solid pill and must not
   * compete with it: two saturated chips side by side read as four colours with no
   * hierarchy, and you have to read the words to tell the columns apart.
   *
   * `text` drops the chip altogether: the toned glyph and the word run inside a line of
   * text, such as a list row's preview, where any box would stack a second shape in the row.
   */
  variant?: 'solid' | 'outline' | 'text';
  /**
   * Let a sentence-long label break onto a second line instead of running off a phone's
   * width. Off by default: a pill in a column stays one line so the column stays aligned.
   */
  wrap?: boolean;
  className?: string;
}

/**
 * A value out of the data, painted by its tone: a task's status, an invoice's state,
 * a vessel's condition. The caller supplies the text, so one component covers every
 * value a field can take.
 *
 * A **badge** is the other thing and is not a variant of this: it states one fixed fact
 * and owns its own name and wording — `PendingBadge` says "Invited", `TemporaryBadge`
 * says "Temporary", `DeclinedBadge` says "Declined" with an optional reason,
 * `OutOfWaterBadge` says "Out of water". Nothing is passed in because there is nothing
 * to vary. Do not fold a badge into this pill and do not hard-code a label here — the
 * two answer different questions, and the codebase keeps both on purpose.
 *
 * @alias badge, chip, tag, label, state pill, priority chip, status chip
 */
export const StatusPill = ({
  label,
  tone = 'neutral',
  icon,
  variant = 'solid',
  wrap = false,
  className,
}: StatusPillProps) => (
  <span
    className={clsx(
      s.pill,
      variant === 'text'
        ? s.text
        : variant === 'outline'
          ? [s.outline, tone === 'neutral' && s.muted]
          : s[tone],
      wrap && s.wrap,
      className,
    )}
  >
    {icon && <span className={clsx(s.icon, variant !== 'solid' && s[`${tone}Ink`])}>{icon}</span>}
    {label}
  </span>
);

export default StatusPill;
